import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import { EMPTY_MEMBER_SUBSET, useMemberSubsetDraft } from 'src/components/star-grid/hooks/subset-editor/useMemberSubsetDraft';
import { useSubsetActions } from 'src/components/star-grid/hooks/subset-editor/useSubsetActions';
import { useSubsetEditorData } from 'src/components/star-grid/hooks/subset-editor/useSubsetEditorData';
import { useSubsetRows } from 'src/components/star-grid/hooks/subset-editor/useSubsetRows';
import type { MemberSubsetModalProps } from 'src/components/star-grid/memberSubset.types';
import { confirmedMemberIds, isResetViewDisabled, optionValue } from 'src/components/star-grid/utils/subset-editor/memberSubsetEditor';

/**
 * The subset editor's state, composed as data → rows → actions. Keep the call order: effects run in it, and
 * forgetting history must follow a saved view's expansion being restored.
 */
export const useMemberSubsetEditor = ({
  hierarchyId,
  hierarchyTree,
  visible,
  onDismiss,
  onConfirm,
  initialChecked,
  hierarchies,
  onHierarchyChange,
  pickOrder = false
}: MemberSubsetModalProps) => {
  // Reopening resumes where OK left off, rather than starting blank.
  const restored = useMemo(() => new Set(initialChecked ?? []), [initialChecked]);
  // Read once at mount, like the draft it seeds: a field that already has members opens on THOSE.
  const openedWithSelection = useRef(restored.size > 0).current;
  const subset = useMemberSubsetDraft({ ...EMPTY_MEMBER_SUBSET, checked: restored });
  const { draft, setChecked, reset, showAll, forgetHistory } = subset;
  const { agGridTheme } = useTheme();
  const [search, setSearch] = useState('');

  const data = useSubsetEditorData(visible, hierarchyId, hierarchyTree, hierarchies, openedWithSelection);
  const rows = useSubsetRows(data.labelled, data.viewRows, draft, search, showAll);

  // Snapshots hold positions, uids and ops from one member universe, so a hierarchy or view switch drops them.
  useEffect(() => {
    forgetHistory();
  }, [data.pickedValue, data.viewId, forgetHistory]);

  const actions = useSubsetActions(rows.index, draft, rows.displayRows, rows.gridRows, rows.memberKeys, subset, {
    aliasPair: data.aliasPair,
    effectiveAlias: data.effectiveAlias,
    viewId: data.viewId,
    setViews: data.setViews,
    setViewId: data.setViewId
  });

  const handleConfirm = (): void => {
    const members = confirmedMemberIds(rows.index, rows.memberKeys, pickOrder);
    // Before onConfirm: the caller may key its member map by hierarchy, so the switch has to land first.
    if (data.picked !== null && data.picked.hierarchy_id !== hierarchyId) onHierarchyChange?.(data.picked);
    onConfirm?.(members, data.memberCount, rows.memberKeys);
    onDismiss();
  };

  const { picked, switchHierarchy, setAlias, setViewId, openingViewId } = data;
  // Wipes the draft: every key in it names members of the previous hierarchy.
  const handleHierarchyChange = useCallback(
    (value: string) => {
      if (picked !== null && optionValue(picked) === value) return;
      switchHierarchy(value);
      setSearch('');
      setChecked([]);
      setAlias(null);
      reset();
    },
    [picked, switchHierarchy, setChecked, setAlias, reset]
  );

  // A view replaces the row set; ticks survive only when the editor opened on the field's own selection.
  const handleViewChange = (value: string | undefined): void => {
    setSearch('');
    if (!openedWithSelection) setChecked([]);
    reset();
    setViewId(value ? Number(value) : null);
  };

  // Back to the view the editor opened with. Restores the view, not the selection.
  const handleResetView = (): void => {
    setSearch('');
    setAlias(null);
    setViewId(openingViewId);
    reset();
  };

  return {
    subset,
    agGridTheme,
    search,
    setSearch,
    data,
    rows,
    actions,
    saveTarget: actions.saveMode === 'save' ? data.views.find((view) => view.id === data.viewId) : undefined,
    resetDisabled: isResetViewDisabled(draft, rows.isSearching, data.alias, data.viewId, data.openingViewId),
    gridKey: `${draft.nonce}:${draft.flat ? 'flat' : 'tree'}:${search.trim().toLowerCase()}`,
    handleConfirm,
    handleHierarchyChange,
    handleViewChange,
    handleResetView
  };
};
